/* @flow */

import type { ProductCardProduct, GAProduct } from "shop-state/types";
import type { BreadcrumbLink } from "@crossroads/ui-components";

import React, { useRef, useContext, useEffect, useState, useMemo } from "react";
import cn from "classnames";
import { useTranslate } from "@awardit/react-use-translate";
import { AnalyticsContext, ProductLink } from "@crossroads/analytics";
import { Square, ProductListMedia, PriceLockPrice, PriceLockLoading } from "@crossroads/ui-components";
import useFormat from "helpers/hooks/use-format";
import useShowPrices from "helpers/use-show-prices";
import { getLowestMSRP } from "helpers/utils";
import { pointsPriceByID, isPointsOnly } from "helpers/points";
import { useUi, MODE } from "helpers/ui";
import { formatConfigColors } from "components/ProductOptions/color";

import styles from "./styles.scss";

type ProductCardProps = {
  product: ProductCardProduct,
  className?: string,
  list?: string,
  position?: number,
  breadcrumbLinks?: $ReadOnlyArray<BreadcrumbLink>,
};

const ProductCard = ({
  product,
  className,
  breadcrumbLinks,
  position,
  list,
}: ProductCardProps): React$Node => {
  const t = useTranslate();
  const gaContext = useContext(AnalyticsContext);
  const gaRef: { current: null | Element } = useRef(null);
  // Default to smallest image
  const [imageSrc, setImageSrc] = useState<string>("");
  const options = product.type === "configurable" ? product.options.items : [];
  const pointsPrice = pointsPriceByID(product.pointsPrices, "vanilla");
  const pointsOnly = isPointsOnly(pointsPrice);
  const msrp = useMemo(() => getLowestMSRP(product), [product]);
  const { price, attributes: { showDiscount } } = product;
  const { showPrices, showPricesRead } = useShowPrices();
  const { formatPrice } = useFormat();
  const { setViewMode } = useUi();

  const mapGaObject = (product: ProductCardProduct): GAProduct => {
    const gaObject = {
      item_id: product.sku, // eslint-disable-line camelcase
      item_name: product.name, // eslint-disable-line camelcase
      item_brand: product.attributes.manufacturer, // eslint-disable-line camelcase
      price: product.price.incVat,
      index: Number(position) + 1,
      item_list_name: list ?? "", // eslint-disable-line camelcase
    };

    if (product.categories === undefined || product.categories.length === 0) {
      return gaObject;
    }

    product.categories.forEach((c, i) => {
      if (i === 0) {
        gaObject.item_category = c.name; // eslint-disable-line camelcase
      }
      else {
        gaObject[`item_category${i + 1}`] = c.name;
      }
    });

    return gaObject;
  };

  useEffect(() => {
    const gaObject = mapGaObject(product);

    if (!gaRef.current) {
      return;
    }

    gaContext.register(gaRef.current, gaObject);
  }, [gaRef]);

  const toObj = {
    pathname: product.url,
    state: {
      hint: {
        type: "product",
        product,
        image: imageSrc,
      },
      breadcrumbLinks,
      qty: 1,
      list,
      position: Number(position) + 1,
    },
  };

  const productObj = {
    name: product.name,
    sku: product.sku,
    price: product.price,
    qty: 1,
    attributes: {
      manufacturer: product.attributes.manufacturer,
    },
    categories: product.categories,
  };

  return (
    <div className={cn(styles.block, className)}>
      <div className={styles.hoverFG} />

      <div className={styles.colors}>
        {options
          .filter(x => Boolean(x.product.attrLabels.color))
          .map((x, i) => (
            <div
              key={i}
              style={{ background: `linear-gradient(${formatConfigColors(x.product.attrLabels.color?.title)})` }} />
          ))}
      </div>

      <div className={styles.header}>
        <ProductListMedia
          className={styles.imageWrapper}
          product={product}
          getCurrentImageSrc={x => setImageSrc(x)}
        />
        <div className={styles.badges}>
          {pointsOnly && <span>{t("PRODUCT.POINTS_ONLY")}</span>}
        </div>
      </div>

      <div className={styles.body}>
        <ProductLink
          className={styles.productLink}
          list={list}
          to={toObj}
          product={productObj}
          innerRef={gaRef}
          position={Number(position) + 1}
        >
          <span className={styles.name}>{product.name}</span>
          <p className={styles.brand}>{product.attributes.manufacturer}</p>
        </ProductLink>
        {showPricesRead && (
          <PriceLockPrice
            price={price}
            pointsPrice={pointsPrice}
            msrp={msrp}
            showPrice={showPrices}
            formatPrice={formatPrice}
            showDiscount={showDiscount}
            view="PRODUCT_CARD"
            handleShowPriceClick={() => setViewMode(MODE.PRICE_LOCK)}
          />
        )}
        {!showPricesRead && <PriceLockLoading />}
      </div>
    </div>
  );
};

export const DummyCard = ({ className }: { className?: string }): React$Node => {
  return (
    <div className={cn(className, styles.productCard, styles.dummy)}>
      <div className={styles.imageWrapper}>
        <div className={styles.image}>
          <Square />
        </div>
      </div>

      <div className={styles.body}>
        <p className={styles.brand}>&nbsp;</p>
        <span className={styles.name}>&nbsp;</span>

        <div>
          <p className={styles.price}>&nbsp;</p>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
